
import React, { useState, useEffect, useRef } from 'react';
import { Paper, List, ListItem, ListItemIcon, ListItemText, Typography, InputBase, Box, alpha, useTheme, Divider, Popover } from '@mui/material';
import {
    TextFieldsRounded,
    TitleRounded,
    FormatSizeRounded,
    ImageRounded,
    TableChartRounded,
    FormatQuoteRounded,
    HorizontalRuleRounded,
    AppsRounded,
    SmartButtonRounded,
    GridViewRounded,
    ViewAgendaRounded,
    ViewModuleRounded,
    ViewCompactRounded,
    BarChartRounded,
    FormatQuoteOutlined,
    StarBorderRounded,
    ArrowDropDownRounded,
    DescriptionRounded,
} from '@mui/icons-material';

const BLOCK_OPTIONS = [
    // Text
    { type: 'text', variant: 'body1', label: 'Normal text', icon: <TextFieldsRounded />, hint: 'Paragraph', category: 'Text' },
    { type: 'text', variant: 'h1', label: 'Heading 1', icon: <TitleRounded />, hint: '# Heading 1', category: 'Text' },
    { type: 'text', variant: 'h2', label: 'Heading 2', icon: <TitleRounded sx={{ fontSize: 20 }} />, hint: '## Heading 2', category: 'Text' },
    { type: 'text', variant: 'h3', label: 'Heading 3', icon: <TitleRounded sx={{ fontSize: 18 }} />, hint: '### Heading 3', category: 'Text' },
    { type: 'text', variant: 'h4', label: 'Heading 4', icon: <TitleRounded sx={{ fontSize: 16 }} />, hint: '#### Heading 4', category: 'Text' },
    { type: 'text', variant: 'subtitle1', label: 'Large text', icon: <FormatSizeRounded />, hint: 'Subtitle', category: 'Text' },
    { type: 'text', variant: 'body2', label: 'Small text', icon: <TextFieldsRounded sx={{ fontSize: 18 }} />, hint: 'Caption', category: 'Text' },

    // Media & Data
    { type: 'image', variant: null, label: 'Image', icon: <ImageRounded />, hint: 'Upload or embed', category: 'Media' },
    { type: 'table', variant: null, label: 'Table', icon: <TableChartRounded />, hint: 'Add a table', category: 'Media' },
    { type: 'card', variant: 'body1', label: 'Card', icon: <AppsRounded />, hint: 'Add team member card', category: 'Media', display: 'card' },
    { type: 'flexible-button', variant: 'button', label: 'Button', icon: <SmartButtonRounded />, hint: 'Add a button', category: 'Media' },
    { type: 'flexible-genericCard', variant: null, label: 'Generic Card', icon: <AppsRounded />, hint: 'Card with icon, title, text', category: 'Media' },
    { type: 'flexible-content', variant: '1', label: 'Content 1', icon: <ViewAgendaRounded />, hint: 'Simple card with icon and text', category: 'Media' },
    { type: 'flexible-content', variant: '2', label: 'Content 2', icon: <ImageRounded />, hint: 'Image with text content', category: 'Media' },
    { type: 'flexible-content', variant: '3', label: 'Content 3', icon: <ViewModuleRounded />, hint: 'Horizontal card with icon/image', category: 'Media' },
    { type: 'flexible-featureCard', variant: null, label: 'Feature Card', icon: <SmartButtonRounded />, hint: 'Horizontal card with icon', category: 'Media' },
    { type: 'flexible-spacer', variant: null, label: 'Spacer', icon: <HorizontalRuleRounded />, hint: 'Add empty space', category: 'Layout' },
    { type: 'flexible-icon', variant: null, label: 'Icon', icon: <StarBorderRounded />, hint: 'Standalone icon with link', category: 'Media' },
    { type: 'flexible-dropdown', variant: null, label: 'Dropdown', icon: <ArrowDropDownRounded />, hint: 'Dropdown menu with links', category: 'Media' },
    { type: 'flexible-form', variant: null, label: 'Form', icon: <DescriptionRounded />, hint: 'Custom contact or lead form', category: 'Media' },

    // Card Grid Layouts
    { type: 'cardgrid', variant: 'grid-3', label: 'Card Grid', icon: <GridViewRounded />, hint: 'Simple cards in a grid', category: 'Cards' },
    { type: 'cardgrid', variant: 'featured-block', label: 'Featured Block Grid', icon: <ViewModuleRounded />, hint: 'Large featured card + supporting cards', category: 'Cards' },
    { type: 'cardgrid', variant: 'image-showcase', label: 'Card with image', icon: <ImageRounded />, hint: 'Profile image + text card', category: 'Cards' },
    { type: 'cardgrid', variant: 'split-line', label: 'Split line cards', icon: <ViewModuleRounded />, hint: 'Two-column split styling', category: 'Cards' },
    { type: 'cardgrid', variant: 'minimal-lines', label: 'Minimal line cards', icon: <HorizontalRuleRounded />, hint: 'Clean cards with top rule', category: 'Cards' },
    { type: 'cardgrid', variant: 'list-numbered', label: 'Numbered Card List', icon: <ViewAgendaRounded />, hint: 'Stacked numbered cards', category: 'Cards' },
    { type: 'statgrid', variant: null, label: 'Stats Block', icon: <BarChartRounded />, hint: 'Metrics with visual display', category: 'Cards' },
    { type: 'testimonialgrid', variant: null, label: 'Testimonials', icon: <FormatQuoteOutlined />, hint: 'Quotes with author & rating', category: 'Cards' },

    // Formatting
    { type: 'text', variant: 'body1', label: 'Blockquote', icon: <FormatQuoteRounded />, hint: '> Quote', category: 'Formatting', blockStyle: 'quote' },
    { type: 'divider', variant: null, label: 'Divider', icon: <HorizontalRuleRounded />, hint: '---', category: 'Layout' },
    { type: 'accordion', variant: null, label: 'Nested Card', icon: <AppsRounded />, hint: 'Accordion layout', category: 'Layout' },
    { type: 'columns', variant: '2', label: '2 Columns', icon: <ViewModuleRounded />, hint: 'Two side-by-side columns', category: 'Layout' },
    { type: 'columns', variant: '3', label: '3 Columns', icon: <ViewModuleRounded />, hint: 'Three side-by-side columns', category: 'Layout' },
];

const BlockPicker = ({ open, anchorEl, onSelect, onClose }) => {
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);

    const filteredOptions = BLOCK_OPTIONS.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        (opt.hint && opt.hint.toLowerCase().includes(search.toLowerCase()))
    );

    useEffect(() => {
        if (open) {
            setSearch('');
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open]);

    // Popover handles click-outside closing automatically

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % filteredOptions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions[activeIndex]) {
                onSelect(filteredOptions[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!open) return null;

    // Group options by category for display
    let currentCategory = '';

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            disableAutoFocus
            disableEnforceFocus
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                elevation: 8,
                sx: {
                    width: 300,
                    maxHeight: 420,
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.2),
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                    mt: 1
                }
            }}
            disableRestoreFocus
        >
            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.15) }}>
                <InputBase
                    inputRef={inputRef}
                    fullWidth
                    placeholder="Search blocks..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setActiveIndex(0); }}
                    onKeyDown={handleKeyDown}
                    startAdornment={
                        <Box sx={{ mr: 1, display: 'flex', color: 'text.disabled' }}>
                            🔍
                        </Box>
                    }
                    sx={{ fontSize: 14, px: 0.5 }}
                />
            </Box>
            <List sx={{ overflowY: 'auto', py: 0.5 }}>
                {filteredOptions.length === 0 ? (
                    <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: 'text.disabled' }}>
                        No blocks found
                    </Typography>
                ) : (
                    filteredOptions.map((opt, idx) => {
                        const isSelected = idx === activeIndex;
                        let showCategoryHeader = false;
                        if (!search && opt.category !== currentCategory) {
                            showCategoryHeader = true;
                            currentCategory = opt.category;
                        }
                        return (
                            <React.Fragment key={`${opt.type}-${opt.variant}-${opt.label}`}>
                                {showCategoryHeader && idx > 0 && (
                                    <Divider sx={{ my: 0.5 }} />
                                )}
                                {showCategoryHeader && (
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            px: 2,
                                            pt: idx > 0 ? 1 : 0.5,
                                            pb: 0.3,
                                            display: 'block',
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            color: 'text.disabled',
                                            letterSpacing: '0.08em'
                                        }}
                                    >
                                        {opt.category}
                                    </Typography>
                                )}
                                <ListItem
                                    button
                                    onClick={() => onSelect(opt)}
                                    onMouseEnter={() => setActiveIndex(idx)}
                                    sx={{
                                        py: 0.7,
                                        px: 2,
                                        borderRadius: 1,
                                        mx: 0.5,
                                        width: 'calc(100% - 8px)',
                                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36, color: isSelected ? 'primary.main' : 'text.secondary' }}>
                                        {opt.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={opt.label}
                                        secondary={opt.hint}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: isSelected ? 600 : 400,
                                            fontSize: '0.85rem'
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                            sx: { color: 'text.disabled', fontSize: '0.72rem' }
                                        }}
                                    />
                                </ListItem>
                            </React.Fragment>
                        );
                    })
                )}
            </List>
        </Popover>
    );
};

export default BlockPicker;
